// Export PDF des classements (général ligue/comité, événement) : un tableau par
// catégorie, avec la mention de l'origine (DartsHub) en en-tête et en pied de page.
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { SITE_URL } from '@/functions/seo';

export interface PdfCategory {
  name: string;
  rows: (string | number)[][];
}

interface LeaderboardPdfOptions {
  title: string;
  subtitle?: string;
  head: string[];
  // 2e ligne d'en-tête optionnelle (lieu + date de chaque tournoi), alignée sur les
  // colonnes de `head` situées après les `subHeadOffset` premières (#, Joueur, PTS).
  subHead?: string[];
  subHeadOffset?: number;
  categories: PdfCategory[];
  filename: string;
}

const ACCENT: [number, number, number] = [51, 153, 136];
const SOURCE_URL = SITE_URL.replace(/^https?:\/\//, '');

export const exportLeaderboardPdf = ({ title, subtitle, head, subHead, subHeadOffset = 3, categories, filename }: LeaderboardPdfOptions) => {
  const doc = new jsPDF({ orientation: head.length > 5 ? 'landscape' : 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const generatedOn = new Date().toLocaleDateString('fr-FR');

  const headRows: any[][] = [head];
  if (subHead && subHead.some(Boolean)) {
    headRows.push([{ content: '', colSpan: subHeadOffset }, ...subHead]);
  }

  doc.setFontSize(16);
  doc.text(title, pageWidth / 2, 15, { align: 'center' });
  let y = 21;
  if (subtitle) {
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(subtitle, pageWidth / 2, y, { align: 'center' });
    y += 5;
  }
  doc.setFontSize(9);
  doc.setTextColor(...ACCENT);
  doc.text(`Source : classement officiel DartsHub (${SOURCE_URL})`, pageWidth / 2, y, { align: 'center' });
  doc.setTextColor(0);
  y += 6;

  // Chaque catégorie démarre sur une nouvelle page (la première reste sous l'en-tête).
  let first = true;
  for (const category of categories) {
    if (!category.rows.length) continue;
    if (!first) {
      doc.addPage();
      y = 15;
    }
    first = false;
    doc.setFontSize(12);
    doc.text(category.name, 14, y);
    autoTable(doc, {
      startY: y + 2,
      head: headRows,
      body: category.rows.map((r) => r.map((c) => String(c ?? ''))),
      theme: 'striped',
      styles: { fontSize: 8, cellPadding: 1.5, halign: 'center', valign: 'middle' },
      headStyles: { fillColor: ACCENT, textColor: 255, halign: 'center' },
      columnStyles: { 0: { cellWidth: 10 }, 1: { halign: 'left' }, 2: { fontStyle: 'bold' } },
      margin: { left: 14, right: 14, bottom: 15 },
      didParseCell: (data) => {
        if (data.section === 'head' && data.row.index === 1) {
          data.cell.styles.fontSize = 6.5;
          data.cell.styles.fontStyle = 'normal';
        }
      },
    });
    y = (doc as any).lastAutoTable.finalY + 10;
  }

  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    const w = doc.internal.pageSize.getWidth();
    const h = doc.internal.pageSize.getHeight();
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(`Classement généré par DartsHub - ${SOURCE_URL} - le ${generatedOn}`, 14, h - 7);
    doc.text(`${i} / ${pages}`, w - 14, h - 7, { align: 'right' });
  }

  doc.save(filename);
};
